import { AlertCircle, AlertTriangle, CheckCircle, HelpCircle, Info, Plus, RefreshCw, SkipForward } from 'lucide-react';
import { useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Badge } from '@/core/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';
import { BulkImportValidateResponse, BulkImportPreviewRow, LegacyImportValidateResponse } from '../../types/translation-types';
import { LanguageImpactCard } from '../molecules/LanguageImpactCard';
import { useLanguageStore } from '../../stores/language-store';
import { cn } from '@/core/lib/utils';

interface ImportPreviewProps {
  validationResult: BulkImportValidateResponse | LegacyImportValidateResponse;
  className?: string;
}

export function ImportPreview({ validationResult, className }: ImportPreviewProps) {
  const { keysToAdd, keysToUpdate, errors, warnings, preview, isValid } = validationResult;
  const valuesImpact = 'valuesImpact' in validationResult ? validationResult.valuesImpact : undefined;

  const fetchLanguages = useLanguageStore(state => state.fetchLanguages);
  useEffect(() => {
    fetchLanguages(); // Ensure languages are loaded for display names
  }, [fetchLanguages]);

  const getActionBadge = (action: BulkImportPreviewRow['action']) => {
    switch (action) {
      case 'add':
        return (
          <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-100">
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Badge>
        );
      case 'update':
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            <RefreshCw className="h-3 w-3 mr-1" />
            Update
          </Badge>
        );
      case 'skip':
        return (
          <Badge variant="secondary">
            <SkipForward className="h-3 w-3 mr-1" />
            Skip
          </Badge>
        );
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="border rounded-lg p-4 bg-green-50">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">Keys to Add</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  Unique translation identifiers being added
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-2xl font-bold text-green-700 mt-1">{keysToAdd}</p>
        </div>

        <div className="border rounded-lg p-4 bg-blue-50">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Keys to Update</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  Existing translation identifiers being updated
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-2xl font-bold text-blue-700 mt-1">{keysToUpdate}</p>
        </div>

        <div className={cn(
          'border rounded-lg p-4',
          isValid ? 'bg-green-50' : 'bg-red-50'
        )}>
          <div className="flex items-center gap-2">
            {isValid ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
            <span className={cn(
              'text-sm font-medium',
              isValid ? 'text-green-700' : 'text-red-700'
            )}>
              {isValid ? 'Valid' : 'Has Errors'}
            </span>
          </div>
          <p className={cn(
            'text-2xl font-bold mt-1',
            isValid ? 'text-green-700' : 'text-red-700'
          )}>
            {errors.length} errors
          </p>
        </div>
      </div>

      {/* Language-Specific Impact */}
      {valuesImpact && valuesImpact.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">Language-Specific Impact</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  Shows exactly how many translation values will be added vs updated for each language.
                  One key can have multiple values (one per language).
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {valuesImpact.map(impact => (
              <LanguageImpactCard
                key={impact.languageCode}
                languageCode={impact.languageCode}
                valuesToInsert={impact.valuesToInsert}
                valuesToUpdate={impact.valuesToUpdate}
                totalValues={impact.totalValues}
              />
            ))}
          </div>
        </div>
      )}

      {/* Errors - Separate duplicates from other errors */}
      {errors.length > 0 && (() => {
        const duplicateErrors = errors.filter(e =>
          e.message.toLowerCase().includes('duplicate') ||
          e.message.toLowerCase().includes('already exists')
        );
        const otherErrors = errors.filter(e =>
          !e.message.toLowerCase().includes('duplicate') &&
          !e.message.toLowerCase().includes('already exists')
        );

        return (
          <>
            {/* Duplicate Key Warnings */}
            {duplicateErrors.length > 0 && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  <p className="font-medium mb-2">Duplicate Keys Found ({duplicateErrors.length}):</p>
                  <p className="text-sm mb-2">These keys already exist and will be skipped during import.</p>
                  <ul className="list-disc pl-4 space-y-1 text-sm">
                    {duplicateErrors.slice(0, 10).map((error, idx) => {
                      const rowNum = error.row ?? error.rowNumber;
                      return (
                        <li key={idx}>
                          <strong>Row {rowNum}:</strong> "{error.keyName}" - {error.message}
                        </li>
                      );
                    })}
                    {duplicateErrors.length > 10 && (
                      <li className="text-yellow-700">
                        ...and {duplicateErrors.length - 10} more duplicates
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Other Errors */}
            {otherErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-2">Errors found in the file ({otherErrors.length}):</p>
                  <ul className="list-disc pl-4 space-y-1 text-sm">
                    {otherErrors.slice(0, 10).map((error, idx) => {
                      const rowNum = error.row ?? error.rowNumber;
                      const location = rowNum ? `Row ${rowNum}` : (error.columnName ? `Column: ${error.columnName}` : 'File');
                      return (
                        <li key={idx}>
                          <strong>{location}:</strong> {error.message}
                          {error.keyName && <span className="text-muted-foreground ml-1">(key: {error.keyName})</span>}
                        </li>
                      );
                    })}
                    {otherErrors.length > 10 && (
                      <li className="text-muted-foreground">
                        ...and {otherErrors.length - 10} more errors
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </>
        );
      })()}

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <p className="font-medium mb-2">Warnings:</p>
            <ul className="list-disc pl-4 space-y-1 text-sm">
              {warnings.slice(0, 5).map((warning, idx) => {
                const rowNum = warning.row ?? warning.rowNumber;
                return (
                  <li key={idx}>
                    {rowNum ? `Row ${rowNum}: ` : ''}{warning.message}
                  </li>
                );
              })}
              {warnings.length > 5 && (
                <li className="text-yellow-600">
                  ...and {warnings.length - 5} more warnings
                </li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Preview Table */}
      {preview && preview.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-muted/50 px-4 py-2 border-b">
            <h3 className="font-medium text-sm">Preview (first 20 rows)</h3>
          </div>
          <div className="max-h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Row</TableHead>
                  <TableHead className="w-[100px]">Action</TableHead>
                  <TableHead>Key Name</TableHead>
                  <TableHead>Languages</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.slice(0, 20).map((row) => (
                  <TableRow key={row.rowNumber}>
                    <TableCell className="text-muted-foreground">{row.rowNumber}</TableCell>
                    <TableCell>{getActionBadge(row.action)}</TableCell>
                    <TableCell className="font-mono text-sm">{row.keyName}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.values.slice(0, 5).map((val) => (
                          <Badge
                            key={val.languageCode}
                            variant={val.isNew ? 'default' : 'outline'}
                            className={cn(
                              'text-xs',
                              val.isNew && 'bg-green-100 text-green-700'
                            )}
                          >
                            {val.languageCode.toUpperCase()}
                          </Badge>
                        ))}
                        {row.values.length > 5 && (
                          <Badge variant="secondary" className="text-xs">
                            +{row.values.length - 5}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {preview.length > 20 && (
            <div className="bg-muted/30 px-4 py-2 border-t text-sm text-muted-foreground text-center">
              Showing 20 of {preview.length} rows
            </div>
          )}
        </div>
      ) : (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Preview data is not available. The file has been validated successfully and is ready to import.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
